/* =========================================================
   Legal contact change requests.

   The legal contact block is read-only everywhere; changes
   arrive here as requests with an appointment document
   attached. Rejecting one always requires a written reason,
   which is stored and shown in the resolved history.
   ========================================================= */
import { useEffect, useReducer, useState } from 'react';
import type { LegalRequest } from '@/types';
import { ic } from '@/components/Icons';
import { BLUE_HEADER, Modal, Section } from '@/components/ui';
import { ds } from '@/data';
import { useI18n } from '@/i18n';
import { legalNewName } from '@/lib/legalDoc';
import { useAppStore } from '@/store/useAppStore';

export function LegalRequests() {
  const { lang, t } = useI18n();
  const addToast = useAppStore((s) => s.addToast);
  const [, force] = useReducer((x: number) => x + 1, 0);
  const [rejecting, setRejecting] = useState<LegalRequest | null>(null);
  const [reason, setReason] = useState('');
  const [viewing, setViewing] = useState<LegalRequest | null>(null);
  /** URL local de la carta que se está mirando, o el motivo de que no haya. */
  const [carta, setCarta] = useState<{ url: string } | { fallo: string } | null>(null);


  // Las bandejas llegan con la hidratación, que ocurre solo al iniciar
  // sesión. Sin esto, una solicitud que un socio acaba de enviar no le
  // aparece al Admin ETC hasta que vuelve a entrar.
  useEffect(() => {
    void ds.refreshRequests().then(force).catch(() => {
      /* si falla se sigue viendo lo que ya había; no vale la pena un aviso */
    });
  }, []);

  const all = ds.listLegalRequests();
  const pending = all.filter((r) => r.status === 'pending');
  const resolved = all.filter((r) => r.status !== 'pending');

  /** Abre la carta que subió el socio.
   *
   *  Antes esto FABRICABA un PDF con el membrete de la organización y el
   *  nombre del nuevo contacto — un documento que nadie había firmado, con
   *  pinta de acta real. Se veía igual tanto si el socio había adjuntado
   *  algo como si no. Ahora se sirve el archivo o se dice que no hay. */
  async function abrirCarta(r: LegalRequest) {
    setCarta(null);
    if (!r.docStored) {
      setCarta({
        fallo: lang === 'es'
          ? 'Esta solicitud no trae carta de nombramiento. Pídesela al socio antes de aprobarla.'
          : 'This request has no appointment letter attached. Ask the partner for it before approving.',
      });
      return;
    }
    try {
      setCarta({ url: await ds.loadLegalDoc(r.id) });
    } catch (err) {
      setCarta({ fallo: err instanceof Error ? err.message : String(err) });
    }
  }

  function download(r: LegalRequest) {
    if (!r.docStored) {
      addToast({
        kind: 'warn',
        title: lang === 'es' ? 'No hay archivo' : 'No file',
        body: r.OrgName,
        details: lang === 'es'
          ? 'Esta solicitud no trae carta de nombramiento.'
          : 'This request has no appointment letter attached.',
      });
      return;
    }
    void ds.loadLegalDoc(r.id).then((url) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = r.doc || 'carta.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      addToast({ kind: 'ok', title: lang === 'es' ? 'Descarga iniciada' : 'Download started', body: r.doc });
    }).catch((err: unknown) => {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se pudo descargar' : 'Download failed',
        body: err instanceof Error ? err.message : String(err),
      });
    });
  }

  function approve(r: LegalRequest) {
    ds.resolveLegalRequest(r.id, 'approved');
    addToast({ kind: 'ok', title: t('admin.approve'), body: r.OrgName });
    force();
  }

  function confirmReject() {
    if (!rejecting) return;
    ds.resolveLegalRequest(rejecting.id, 'rejected', reason.trim());
    addToast({ kind: 'ok', title: t('admin.reject'), body: rejecting.OrgName, details: reason.trim() });
    setRejecting(null);
    setReason('');
    force();
  }

  return (
    <Section title={t('admin.requests')} tone="var(--warn)" right={<span className="chip warn">{pending.length}</span>}>
      {pending.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          {lang === 'es' ? 'Sin solicitudes pendientes.' : 'No pending requests.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {pending.map((r) => (
            <div
              key={r.id}
              className="card m-grid1"
              style={{ padding: 14, borderLeft: '3px solid var(--warn)', display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 14, alignItems: 'center' }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{r.OrgName}</div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{r.OrgId}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
                  {lang === 'es' ? 'Solicitó' : 'Requested by'}: <span style={{ fontWeight: 600 }}>{r.requestedBy || '—'}</span> · {r.date}
                </div>
              </div>
              <div style={{ fontSize: 12 }}>
                <div style={{ color: 'var(--text-3)' }}>{lang === 'es' ? 'Nuevo contacto' : 'New contact'}</div>
                <div style={{ fontWeight: 600 }}>{legalNewName(r)}</div>
                <div style={{ color: 'var(--text-2)' }}>{r.email}</div>
                {r.docStored ? (
                  <button
                    className="mono btn ghost"
                    onClick={() => download(r)}
                    data-tip={lang === 'es' ? 'Descargar la carta' : 'Download the letter'}
                    style={{ fontSize: 11, padding: 0, color: 'var(--brand-primary)', gap: 4 }}
                  >
                    {ic.download} {r.doc}
                  </button>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 2 }}>
                    {lang === 'es' ? 'Sin carta de nombramiento' : 'No appointment letter'}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  className="btn sm"
                  onClick={() => { setViewing(r); void abrirCarta(r); }}
                  data-tip={lang === 'es' ? 'Ver la carta de nombramiento' : 'View the appointment letter'}
                >
                  {ic.eye} {lang === 'es' ? 'Ver carta' : 'View letter'}
                </button>
                <button className="btn sm danger" onClick={() => { setRejecting(r); setReason(''); }}>
                  {ic.close} {t('admin.reject')}
                </button>
                <button className="btn sm primary" onClick={() => approve(r)}>
                  {ic.check} {t('admin.approve')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* resolved history */}
      {resolved.length ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            {lang === 'es' ? 'Resueltas' : 'Resolved'} · {resolved.length}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {resolved.slice(0, 10).map((r) => (
              <div
                key={r.id}
                className="card"
                style={{ padding: 12, borderLeft: `3px solid ${r.status === 'approved' ? 'var(--ok)' : 'var(--err)'}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <span className={'chip ' + (r.status === 'approved' ? 'ok' : 'err')} style={{ flexShrink: 0, marginTop: 1 }}>
                  {r.status === 'approved' ? (lang === 'es' ? 'Aprobada' : 'Approved') : lang === 'es' ? 'Rechazada' : 'Rejected'}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {r.OrgName} <span className="mono" style={{ fontWeight: 500, fontSize: 11, color: 'var(--text-3)' }}>· {r.OrgId}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                    {legalNewName(r)} · {r.email} · {r.resolvedDate}
                  </div>
                  {r.status === 'rejected' && r.rejectReason ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text)', background: 'var(--st-req-l)', borderRadius: 6, padding: '7px 10px' }}>
                      <span style={{ fontWeight: 700, color: 'var(--err)' }}>
                        {lang === 'es' ? 'Motivo del rechazo: ' : 'Rejection reason: '}
                      </span>
                      {r.rejectReason}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* La carta tal como la subió el socio. */}
      <Modal
        open={!!viewing}
        onClose={() => { setViewing(null); setCarta(null); }}
        width={760}
        headerStyle={BLUE_HEADER}
        title={viewing ? (viewing.doc || (lang === 'es' ? 'Carta de nombramiento' : 'Appointment letter')) : ''}
        footer={
          <>
            <button className="btn" onClick={() => { setViewing(null); setCarta(null); }}>
              {t('common.close')}
            </button>
            {viewing?.docStored ? (
              <button className="btn primary" onClick={() => viewing && download(viewing)}>
                {ic.download} {lang === 'es' ? 'Descargar' : 'Download'}
              </button>
            ) : null}
          </>
        }
      >
        {viewing ? (
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
              <strong>{viewing.OrgName}</strong>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}> · {viewing.OrgId}</span>
              {' · '}
              {lang === 'es' ? 'Nuevo contacto' : 'New contact'}: <strong>{legalNewName(viewing)}</strong>
            </div>
            {carta && 'url' in carta ? (
              viewing.doc.toLowerCase().endsWith('.docx') ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
                  {lang === 'es'
                    ? 'Es un DOCX: el navegador no lo muestra aquí. Descárgalo para leerlo.'
                    : 'This is a DOCX: the browser cannot show it here. Download it to read it.'}
                </div>
              ) : (
                <object
                  data={carta.url}
                  type="application/pdf"
                  style={{ width: '100%', height: '58vh', border: '1px solid var(--line-2)', borderRadius: 6 }}
                >
                  <div style={{ padding: 24, fontSize: 13 }}>
                    {lang === 'es'
                      ? 'Tu navegador no puede mostrar el PDF aquí. Usa Descargar.'
                      : 'Your browser cannot display the PDF here. Use Download.'}
                  </div>
                </object>
              )
            ) : carta && 'fallo' in carta ? (
              <div
                role="alert"
                style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start', padding: '12px 14px',
                  border: '1px solid var(--warn)', borderRadius: 8, fontSize: 12.5,
                }}
              >
                <span style={{ flexShrink: 0, display: 'inline-flex' }}>{ic.warn}</span>
                <span>{carta.fallo}</span>
              </div>
            ) : (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                {lang === 'es' ? 'Abriendo…' : 'Opening…'}
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* reject modal — the reason is mandatory and gets stored */}
      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={lang === 'es' ? 'Rechazar solicitud de cambio' : 'Reject change request'}
        footer={
          <>
            <button className="btn" onClick={() => setRejecting(null)}>{t('common.cancel')}</button>
            <button className="btn danger" disabled={!reason.trim()} onClick={confirmReject}>
              {ic.close} {lang === 'es' ? 'Rechazar y guardar' : 'Reject and save'}
            </button>
          </>
        }
      >
        {rejecting ? (
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
              {lang === 'es' ? (
                <>Solicitud de <strong>{rejecting.OrgName}</strong> — nuevo contacto <strong>{legalNewName(rejecting)}</strong>.</>
              ) : (
                <>Request from <strong>{rejecting.OrgName}</strong> — new contact <strong>{legalNewName(rejecting)}</strong>.</>
              )}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {lang === 'es' ? 'Razón del rechazo' : 'Rejection reason'} <span style={{ color: 'var(--err)' }}>*</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder={
                  lang === 'es'
                    ? 'Explica por qué se rechaza esta solicitud (documento ilegible, datos incompletos, etc.)'
                    : 'Explain why this request is rejected (illegible document, incomplete data, etc.)'
                }
                style={{ padding: '9px 11px', border: '1px solid var(--line-2)', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', width: '100%' }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {lang === 'es'
                  ? 'El motivo se guarda y queda visible en el historial de solicitudes resueltas.'
                  : 'The reason is saved and shown in the resolved requests history.'}
              </span>
            </label>
          </div>
        ) : null}
      </Modal>
    </Section>
  );
}
